import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import { useConfirm } from '../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Select, Textarea, Field } from './ui';

const EMPTY_FORM = { name: '', subscription_type: 'monthly', duration_days: 30, price: '', description: '', is_active: true };

function ModulePaymentPlans({ moduleId }) {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchPlans = () => {
    setLoading(true);
    enrollmentService.getSubscriptions({ mine: 1, module: moduleId })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setPlans(list.filter((p) => String(p.module) === String(moduleId)));
      })
      .catch(() => addNotification(t('Failed to load payment plans'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchPlans, [moduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, module: moduleId };
      if (editing) {
        const res = await enrollmentService.updateSubscription(editing.id, payload);
        setPlans((prev) => prev.map((p) => (p.id === editing.id ? res.data : p)));
      } else {
        const res = await enrollmentService.createSubscription(payload);
        setPlans((prev) => [...prev, res.data]);
      }
      addNotification(t('Payment plan saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save plan'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!(await confirm(t('Delete plan "{{name}}"?', { name: p.name })))) return;
    try {
      await enrollmentService.deleteSubscription(p.id);
      setPlans((prev) => prev.filter((x) => x.id !== p.id));
      addNotification(t('Plan deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete plan (it may be in use by enrollments)'), 'error');
    }
  };

  const typeLabels = { monthly: t('Monthly'), term: t('Term'), yearly: t('Yearly') };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{t('Payment Plans for This Course')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('Once this course has any plan of its own, students enrolling see only these — the platform-wide defaults stop being offered.')}</p>
        </div>
        <Button size="sm" onClick={openCreate}>+ {t('Add Plan')}</Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('Loading...')}</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-gray-500">{t('No custom plans yet — students will see the platform-wide default plans instead.')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {plans.map((p) => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                  <p className="text-xs text-gray-500">{typeLabels[p.subscription_type] || p.subscription_type} · {t('{{count}} days', { count: p.duration_days })}</p>
                </div>
                <Badge tone={p.is_active ? 'green' : 'gray'}>{p.is_active ? t('Active') : t('Inactive')}</Badge>
              </div>
              <p className="text-lg font-bold text-gray-800 mt-2">${p.price}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="secondary" onClick={() => openEdit(p)}>{t('Edit')}</Button>
                <Button size="sm" variant="danger" onClick={() => remove(p)}>{t('Delete')}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Plan') : t('New Plan')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('Type')} required>
              <Select value={form.subscription_type} onChange={(e) => setForm({ ...form, subscription_type: e.target.value })}>
                <option value="monthly">{t('Monthly')}</option>
                <option value="term">{t('Term')}</option>
                <option value="yearly">{t('Yearly')}</option>
              </Select>
            </Field>
            <Field label={t('Duration (days)')} required>
              <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Price')} required>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </Field>
          <Field label={t('Description')}>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active (visible to students)')}
          </label>
        </div>
      </Modal>
    </Card>
  );
}

export default ModulePaymentPlans;
